<template>
  <div data-testid="data-table-action" :class="rootClasses">
  <!-- Filter toolbar - hidden by default, use showToolbar prop to show -->
  <div v-if="showToolbar" data-testid="data-table-action__filter-toolbar" class="data-table-action__filter-toolbar">
    <v-btn 
      data-testid="data-table-action__clear-filter-btn"
      v-if="hasActiveFilter && showClearFilter" 
      @click="clearfilter()" 
      text
      class="data-table-action__clear-filter-btn"
    >
      <span>Clear Filter</span>
    </v-btn>
  </div>

  <v-card data-testid="data-table-action__card" class="shadow-off data-table-action__card">
    <div>
      <v-skeleton-loader
        data-testid="data-table-action__skeleton-loader"
        v-if="loading"
        type="table-heading, list-item-two-line, article, article, article"
        class="my-4"
        :height="autoHeight ? undefined : tableHeight"
      />
    <v-simple-table
      data-testid="data-table-action__table"
      class="data-table-action__table simple-table border-on"
      :class="{ 'data-table-action__table--truncate-cells': enableCellTruncate }"
      :height="autoHeight ? undefined : computedTableHeight"
      v-else
    >
      <template v-slot:default>
        <thead data-testid="data-table-action__thead" :class="[headerBgClass, theadClass]">
          <tr data-testid="data-table-action__header-row" :class="['py-2', headerBgClass, headerRowClass]">
            <th
              data-testid="data-table-action__selectall-header"
              :class="getSelectAllHeaderClass()"
              :data-sticky-seq="bulkActions && stickyColumnCount > 0 ? '0' : undefined"
              v-if="bulkActions"
            >
              <v-checkbox 
                data-testid="data-table-action__selectall-checkbox"
                dense 
                color="primary"
                :indeterminate="isDataSelected && !selectAll"
                :ripple="false"
                :disabled="selectAllDisabled"
                class="ma-0 py-1 single-line data-table-action__selectall-checkbox" 
                hide-details
                v-model="selectAll" 
                @change="onSelectAllChange"
              ></v-checkbox>
            </th>
            
            <template v-for="(item, index) in computedHeaders">
              <th 
                data-testid="data-table-action__header-cell"
                :class="getHeaderCellClass(item, index)" 
                :key="item.value"
                :ref="'header' + index" 
                v-if="item.visible"
                :data-column-value="item.value"
                :data-sticky-seq="getHeaderStickySeq(index)"
                :style="getHeaderColumnStyle(item)"
                @dragover.prevent="onDragOver(index, $event)"
                @dragenter.prevent="onDragOver(index, $event)"
                @dragleave="onDragLeave(index, $event)"
                @drop.prevent="onDrop(index)"
              >
                <div class="all-width all-height d-flex align-center">
                  <div data-testid="data-table-action__header-content" class="d-flex all-width align-center">
                    <div
                      class="data-table-action__header-content-left header-drag-zone d-flex align-center"
                      :draggable="enableColumnReorder"
                      @dragstart="startDrag(index, $event)"
                      @dragend="onDragEnd"
                    >
                      <span
                        data-testid="data-table-action__header-text"
                        class="data-table-action__header-text ma-0 pa-0"
                        :class="[item.headerClass]"
                      >
                        {{ item.text }}
                      </span>
                    </div>
                    
                    <v-menu 
                      data-testid="data-table-action__column-menu"
                      v-model="menuOpen[index]"
                      :disabled="columnActionsDisabled"
                      :close-on-content-click="false" 
                      offset-y
                      fixed
                      :z-index="columnFilterMenuZIndex"
                      transition="slide-y-transition" 
                      :ref="'menu'+index"
                      content-class="elevation-3 data-table-action__column-menu-content"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          class="data-table-action__column-actions-activator"
                          v-bind="attrs"
                          v-on="on"
                          @click="openColumnActions(item, index)"
                          @dragstart.stop
                          @dragend.stop
                        >
                          <FcTooltip
                            text="Column Actions"
                            :disabled="!!menuOpen[index]"
                            data-testid="data-table-action__column-actions-tooltip"
                            activator-class="data-table-action__column-actions-tooltip-activator"
                          >
                            <v-btn 
                              data-testid="data-table-action__column-actions-btn"
                              v-if="item.sortable || item.filterable" 
                              icon
                              :disabled="columnActionsDisabled"
                              class="ml-1 data-table-action__column-actions-btn"
                              :class="{ 'data-table-action__column-actions-btn--active': clearIfEmptyArrays[item.value] }"
                            >
                              <span class="data-table-action__column-actions-icon-wrap">
                                <img
                                  src="/img/icons/fbc-icons/filter.svg"
                                  alt=""
                                  class="data-table-action__column-actions-icon"
                                  data-testid="data-table-action__column-actions-icon"
                                >
                                <span
                                  v-if="clearIfEmptyArrays[item.value]"
                                  class="data-table-action__column-actions-badge"
                                  data-testid="data-table-action__column-actions-badge"
                                  aria-hidden="true"
                                ></span>
                              </span>
                            </v-btn>
                          </FcTooltip>
                        </span>
                      </template>

                      <ColumnFilter
                        data-testid="data-table-action__column-filter"
                        v-if="item.filterable"
                        :header="item"
                        :table-data="filterSourceData"
                        :preserve-options-on-filter="!hasFilterOptionsData"
                        :selected-values="selectedFilters[item.value] || []"
                        :none-sentinel="noneSentinel"
                        :show-apply-button="showFilterApplyButton"
                        :show-save-button="computedShowFilterSaveButton"
                        :is-open="menuOpen[index]"
                        auto-open
                        @apply="vals => { selectedHeader = item; handleFilterApply(vals, index); }"
                        @save="vals => { selectedHeader = item; handleFilterSave(vals, index); }"
                        @close="closeMenu(index)"
                      />
                    </v-menu>
                  </div>
                  
                  <span 
                    data-testid="data-table-action__resizer-handle"
                    v-if="item.value != 'action'" 
                    class="resizer-handle"
                    @mousedown.stop="initResize($event, index)"
                    @dragstart.stop
                    @dragend.stop
                  ></span>
                </div>
              </th>
            </template>
          </tr>
        </thead>
        
        <tbody data-testid="data-table-action__tbody" class="data-table-action__tbody">
          <!-- Empty state when no data -->
          <tr v-if="isTableEmpty" data-testid="data-table-action__empty-row" class="data-table-action__empty-row">
            <td 
              :colspan="totalColumnCount" 
              class="data-table-action__empty-cell"
              data-testid="data-table-action__empty-cell"
            >
              <div class="data-table-action__empty-content">
                <v-icon 
                  data-testid="data-table-action__empty-icon"
                  size="40" 
                  color="grey lighten-1"
                  class="mb-2"
                >
                  {{ emptyStateIcon }}
                </v-icon>
                <p 
                  data-testid="data-table-action__empty-title"
                  class="text-subtitle-1 grey--text text--darken-1 mb-1 ma-0"
                >
                  {{ emptyStateTitle }}
                </p>
                <p
                  v-if="emptyStateSubtitle"
                  data-testid="data-table-action__empty-subtitle"
                  class="caption grey--text mb-0 ma-0"
                >
                  {{ emptyStateSubtitle }}
                </p>
              </div>
            </td>
          </tr>
          <!-- Normal data rows -->
          <slot v-else name="tbody" :headers="computedHeaders" />
        </tbody>
      </template>
    </v-simple-table>
     </div>
  </v-card>
  <FcTooltip
    v-if="enableCellTruncate && enableCellTruncateTooltip"
    ref="cellTooltipRef"
    positioned
    :value="cellTooltip.show"
    :position-x="cellTooltip.x"
    :position-y="cellTooltip.y"
    placement="bottom"
    :content-class="cellTooltipContentClasses"
    :text="cellTooltip.text"
    data-testid="data-table-action__cell-tooltip"
  />
</div>
</template>
<script>
import './DataTableAction.scss';
import ColumnFilter from './ColumnFilter.vue';
import FcTooltip from '@/modules/financialclose/components/FcTooltip/FcTooltip.vue';
import { CellTruncationManager } from './CellTruncationManager';
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEBOUNCE_FILTER_MS,
  DEFAULT_COLUMN_MAX_WIDTH,
  NONE_SENTINEL,
  LOG_PREFIX,
  LINK_HOVER_CELL_CLASS,
  COLUMN_FILTER_MENU_Z_INDEX,
} from './constants.js';
export default {
  props: {
    headers: {
      type: Array,
      required: true,
    },
    theadClass: {
      type: [String, Array, Object],
      default: ''
    },
    headerRowClass: {
      type: [String, Array, Object],
      default: ''
    },
    headerCellClass: {
      type: [String, Array, Object],
      default: ''
    },
    /** Header background class — override per entity (default keeps existing look). */
    headerBgClass: {
      type: [String, Array, Object],
      default: 'fbc-table-header-bg',
    },
    tabledata: {
      type: Array,
      required: true,
    },
    originalData: {
      type: Array,
      default: null,
    },
    /**
     * Dataset for column filter checkbox/range options (pre-column-filter scope).
     * When set with rows, used instead of tabledata so applying a column filter does not
     * shrink available values. Must match the rows visible before column filtering
     * (e.g. stage-filtered list), not the fully unscoped originalData.
     * Null or an empty array falls back to tabledata.
     */
    filterOptionsData: {
      type: Array,
      default: null,
    },
    itemsperpage:{
        type:Number,
        default: DEFAULT_ITEMS_PER_PAGE
    },
    searchterm:{
        type:String,
        default:''
    },
    selectAllFunction:{
      type:Function,
      default: null
    },
    isDataSelected:{
      type:Boolean,
      default:false
    },
    bulkActions:{
      type:Boolean,
      default:true
    },
    checkalldata:{
      type:Boolean,
      default:false,
    },
    /** Disable the header select-all checkbox (e.g. while rows are still loading). */
    selectAllDisabled: {
      type: Boolean,
      default: false,
    },
    /** Disable the per-column filter / sort menu. */
    columnActionsDisabled: {
      type: Boolean,
      default: false,
    },
    selectfilter:{
      type:Object,
      default: () => ({}),
      validator: value => typeof value === 'object' && value !== null && !Array.isArray(value),
    },
    page_details:{
      type:Object,
      default: () => ({}),
    },
    /**
     * @deprecated Use showFilterSaveButton instead. Kept for backward compatibility.
     */
    enableSaveOption: {
      type: Boolean,
      default: false
    },
    /**
     * Whether to show the Apply button in filter dropdown (applies filter without persisting)
     */
    showFilterApplyButton: {
      type: Boolean,
      default: true
    },
    /**
     * Whether to show the Save button in filter dropdown (persists filter to backend)
     */
    showFilterSaveButton: {
      type: Boolean,
      default: false
    },
    showClearFilter: {
      type: Boolean,
      default: true
    },
    /**
     * Whether to show the built-in filter toolbar row above the table.
     * Set to false to hide and use external clear filter button in parent.
     */
    showToolbar: {
      type: Boolean,
      default: false
    },
    /**
     * When true, column headers can be dragged to reorder columns.
     */
    enableColumnReorder: {
      type: Boolean,
      default: true,
    },
    /**
     * When true, columns use a default max-width and cell text/tags truncate with tooltip on overflow.
     */
    enableCellTruncate: {
      type: Boolean,
      default: true,
    },
    /**
     * Default max column width (px) when header.maxWidth is not set.
     */
    defaultColumnMaxWidth: {
      type: Number,
      default: DEFAULT_COLUMN_MAX_WIDTH,
    },
    /**
     * When true, truncated cells show a Vuetify tooltip (not only the native title attribute).
     */
    enableCellTruncateTooltip: {
      type: Boolean,
      default: true,
    },
    /**
     * When true, the table uses flex layout to automatically fill available parent space.
     * This eliminates gaps between table content and pagination.
     * When false, uses explicit tableHeight prop for height control.
     * @default true
     */
    autoHeight: {
      type: Boolean,
      default: true
    },
    /**
     * Table height - only used when autoHeight is false.
     * Can be a fixed value like '500px' or responsive like 'calc(100vh - 300px)'.
     * Ignored when autoHeight is true (flex layout controls height).
     */
    tableHeight: {
      type: String,
      default: 'calc(100vh - 320px)'
    },
    /**
     * Optional: Number of rows actually displayed in the slot.
     * Use this when tabledata contains unfiltered data but slot shows filtered rows.
     * When provided, this overrides tabledata.length for empty state detection.
     */
    displayedRowCount: {
      type: Number,
      default: null
    },
    emptyTitle: {
      type: String,
      default: '',
    },
    emptySubtitle: {
      type: String,
      default: undefined,
    },
    /**
     * Number of leading header cells (including bulk checkbox when enabled) that stay
     * sticky during horizontal scroll. 0 disables Vue-managed sticky header classes.
     */
    stickyColumnCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
        localHeaders: [],
        selectedFilters:{},
        selectedFilterItems: [],
        filterStage:0,
        selectItem:[],
        selectAll:false,
        currentIndex: null,
        startX: 0,
        draggingIndex: null,
        dropTargetIndex: null,
        startWidth: 0,
        searchfilters:'',
  filteredColumnItems:[],
  selectedHeader:null,
        lastAppliedFilterSignature: "",   // NEW: to avoid re-applying same filter
        pendingFilter: null,              // NEW: for debouncing
  // selectAllStatus removed in favor of computed visible-only helpers
        noneSentinel: NONE_SENTINEL, // Sentinel to represent "no values selected"
        applyFilterOnChange: true, // Watcher guard flag
        mountedOnce:false, // Track if mounted already ran
        menuOpen: [],  // Track open state for each v-menu
        loading:false,
        debounceFilterMs: DEBOUNCE_FILTER_MS,
        // Track whether filter was saved to backend (vs just applied locally)
        isFilterSavedToBackend: false,
        lastBodySyncOrderKey: null,
        slotUsesScopedHeaders: undefined,
        cellTooltip: {
          show: false,
          text: '',
          x: 0,
          y: 0,
        },
        cellTruncation: null,
    };
  },
  created() {
    this.cellTruncation = CellTruncationManager.create({
      getRootEl: () => this.$el,
      getHeaderRef: (index) => this.$refs['header' + index]?.[0],
      nextTick: (fn) => {
        this.$nextTick(fn);
      },
      getEnableCellTruncate: () => this.enableCellTruncate,
      getEnableCellTruncateTooltip: () => this.enableCellTruncateTooltip,
      getDefaultColumnMaxWidth: () => this.defaultColumnMaxWidth,
      getBulkActions: () => this.bulkActions,
      getIsTableEmpty: () => this.isTableEmpty,
      getComputedHeaders: () => this.computedHeaders,
      getScopedSlots: () => this.$scopedSlots,
      getCellTooltip: () => this.cellTooltip,
      setCellTooltip: (tooltip) => {
        this.cellTooltip = tooltip;
      },
      getTooltipContentEl: () => this.resolveCellTooltipContentEl(),
    });
  },
  updated() {
    this.$nextTick(() => {
      this.cellTruncation?.onUpdated(
        this.hasCustomTbodySlot(),
        this.isTableEmpty,
      );
      if ((this.menuOpen || []).some(Boolean)) {
        this.$emit('restore-sticky-column-classes');
      }
    });
  },
  mounted() {
    // Initialize local headers copy
    this.localHeaders = JSON.parse(JSON.stringify(this.headers));
    // Initial load forces application
    this._initFilters({ force:true });
    this.mountedOnce = true;
    this.$nextTick(() => {
      this.slotUsesScopedHeaders = this.detectSlotUsesScopedHeaders();
      this.cellTruncation?.init();
    });
  },
  activated() {
    // When returning via <keep-alive>, avoid forcing if nothing changed
    this._initFilters({ force:false });
    this.$nextTick(() => {
      this.cellTruncation?.onActivated();
    });
  },
  beforeDestroy() {
    // Clean up event listeners to prevent memory leaks
    document.documentElement.removeEventListener('mousemove', this.doResize, false);
    document.documentElement.removeEventListener('mouseup', this.stopResize, false);
    this.cellTruncation?.destroy();
    // Clear any pending filter debounce timer
    if (this._filterTimer) {
      clearTimeout(this._filterTimer);
      this._filterTimer = null;
    }
    // Reset component state to prevent stale data on re-entry
    this.selectedFilters = {};
    this.selectedHeader = null;
    this.menuOpen = [];
    this.filterStage = 0;
  },
  components: { ColumnFilter, FcTooltip },
  computed: {
    columnFilterMenuZIndex() {
      return COLUMN_FILTER_MENU_Z_INDEX;
    },
    /**
     * Dynamic root element classes based on autoHeight mode
     */
    rootClasses() {
      return {
        'data-table-action': true,
        'data-table-action--auto-height': this.autoHeight,
        'data-table-action--fixed-height': !this.autoHeight,
        'data-table-action--column-menu-open': (this.menuOpen || []).some(Boolean),
      };
    },
    cellTooltipContentClass() {
      return `data-table-action__cell-tooltip--${this._uid}`;
    },
    cellTooltipContentClasses() {
      return `data-table-action__cell-tooltip ${this.cellTooltipContentClass}`;
    },
    getHeaderCellClass() {
      return (item, index) => {
        const stickyMeta = this.getHeaderStickyMeta(index);
        return [
          this.headerBgClass,
          'resizable-header',
          'th-min-width-5vh',
          'data-table-action__header-cell',
          this.headerCellClass,
          item.headerClass,
          {
            'data-table-action__header-cell--reorderable': this.enableColumnReorder,
            'data-table-action__header-cell--dragging': this.draggingIndex === index,
            'data-table-action__header-cell--drop-target':
              this.dropTargetIndex === index &&
              this.draggingIndex !== null &&
              this.draggingIndex !== index,
            'data-table-action__header-cell--menu-open': !!this.menuOpen[index],
            'data-table-action__header-cell--filter-active': !!this.clearIfEmptyArrays[item.value],
            'dyn-sticky-col': !!stickyMeta,
            'dyn-sticky-col-edge': !!(stickyMeta && stickyMeta.isEdge),
          },
        ];
      };
    },
    computedHeaders() {
      // Use localHeaders if populated (after drag), otherwise use prop
      const headers = this.localHeaders.length > 0 ? this.localHeaders : this.headers;
      return headers.map((header) => {
        if (!header?.linkHover) return header;
        const existing = header.cellClass || '';
        if (existing.includes(LINK_HOVER_CELL_CLASS)) return header;
        return {
          ...header,
          cellClass: existing
            ? `${existing} ${LINK_HOVER_CELL_CLASS}`
            : LINK_HOVER_CELL_CLASS,
        };
      });
    },
    /**
     * Computed property to determine if Save button should be shown.
     * Supports both new showFilterSaveButton prop and legacy enableSaveOption prop.
     */
    computedShowFilterSaveButton() {
      // New prop takes precedence, fallback to legacy prop for backward compatibility
      return this.showFilterSaveButton || this.enableSaveOption;
    },
    /**
     * True when caller supplied a non-empty filterOptionsData override.
     * Empty arrays are treated as unset (e.g. initial load) so we fall back to tabledata.
     */
    hasFilterOptionsData() {
      return Array.isArray(this.filterOptionsData) && this.filterOptionsData.length > 0;
    },
    /**
     * Dataset for column filter option lists.
     * Prefer filterOptionsData (scoped, pre-column-filter); fall back to tabledata.
     * originalData is only for empty-state detection, not filter options.
     */
    filterSourceData() {
      return this.hasFilterOptionsData ? this.filterOptionsData : this.tabledata;
    },
    tableWrapperStyle() {
      return this.tabledata.length > 0
        ? {height: '700px', overflowY: 'auto'}
        : {height: 'auto', overflowY: 'visible'};
    },
    selectedCheckboxCount() {
      return this.filteredColumnItems.filter(item => item.status).length;
    },
    hasActiveFilter() {
      // Active if any column has non-empty array, NONE_SELECTED sentinel, or a range object with at least one bound
      return Object.values(this.selectedFilters || {}).some(v => {
        if (!v) return false;
        if (Array.isArray(v)) return v.length > 0;
        // date-range { from, to } or number-range { min, max }: active if any bound is set
        if (typeof v === 'object') return Object.values(v).some(bound => bound !== null && bound !== undefined && bound !== '');
        return false;
      });
    },
    clearIfEmptyArrays() {
      // Use local selectedFilters so the red active badge updates immediately on apply
      const filteredHeader = {};
      Object.entries(this.selectedFilters || {}).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        if (Array.isArray(value) && value.length === 0) return;
        // For range objects: only mark active if at least one bound is set
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          if (Object.values(value).every(v => v === null || v === undefined || v === '')) return;
        }
        filteredHeader[key] = value;
      });
      return filteredHeader;
    },
    /**
     * Determines if the table is empty (no data to display).
     * Uses displayedRowCount prop if provided (for cases where tabledata differs from slot content),
     * otherwise falls back to tabledata.length.
     */
    isTableEmpty() {
      // If displayedRowCount prop is explicitly provided (including 0), use it
      if (this.displayedRowCount !== null && this.displayedRowCount !== undefined) {
        return this.displayedRowCount === 0;
      }
      // Otherwise fall back to tabledata length
      return !this.tabledata || this.tabledata.length === 0;
    },
    /**
     * Checks if original data exists but is filtered out
     * (i.e., we have source data but filters reduced it to zero)
     */
    isFilteredEmpty() {
      // Check if we have source data (either from originalData prop or tabledata)
      const sourceData = this.originalData || this.tabledata;
      const hasSourceData = sourceData && sourceData.length > 0;
      // Table is filtered empty if: display is empty + we have active filters + source has data
      return this.isTableEmpty && this.hasActiveFilter && hasSourceData;
    },
    /**
     * Total column count for colspan (includes bulk action checkbox if enabled)
     */
    totalColumnCount() {
      const visibleHeaders = this.computedHeaders.filter(h => h.visible !== false);
      return this.bulkActions ? visibleHeaders.length + 1 : visibleHeaders.length;
    },
    /**
     * Icon to display in empty state
     */
    emptyStateIcon() {
      return this.isFilteredEmpty ? 'mdi-filter-off-outline' : 'mdi-table-off';
    },
    /**
     * Title message for empty state
     */
    emptyStateTitle() {
      if (this.emptyTitle) return this.emptyTitle;
      return this.isFilteredEmpty
        ? 'No data for applied filters'
        : 'No data to show';
    },
    /**
     * Subtitle/helper message for empty state
     */
    emptyStateSubtitle() {
      if (this.emptySubtitle !== undefined && this.emptySubtitle !== null) {
        return this.emptySubtitle;
      }
      return this.isFilteredEmpty
        ? 'Try adjusting or clearing your filters to see more results.'
        : 'There is no data available at this time.';
    },
    /**
     * Computed table height - behavior depends on autoHeight mode
     * - autoHeight=true: Always returns null (flex layout handles sizing)
     * - autoHeight=false: Returns 'auto' when empty, otherwise tableHeight prop
     */
    computedTableHeight() {
      // In autoHeight mode, let flex layout control height (no explicit height)
      if (this.autoHeight) {
        return this.isTableEmpty ? 'auto' : null;
      }
      // In fixed height mode, use tableHeight prop unless empty
      return this.isTableEmpty ? 'auto' : this.tableHeight;
    },
  },
  watch: {
    /**
     * Keep header checkbox in sync when selection is driven from outside
     * (row checkboxes, clear selection, bulk bar dismiss).
     */
    checkalldata: {
      handler(val) {
        this.selectAll = Boolean(val);
      },
      immediate: true,
    },
    headers: {
      handler(newVal) {
        // Sync localHeaders when headers prop changes
        this.localHeaders = JSON.parse(JSON.stringify(newVal));
        this.lastBodySyncOrderKey = null;
        this.scheduleTruncationRefresh();
      },
      deep: true,
      immediate: false
    },
    tabledata() {
      this.lastBodySyncOrderKey = null;
      this.scheduleTruncationRefresh();
    },
    displayedRowCount() {
      this.lastBodySyncOrderKey = null;
      this.scheduleTruncationRefresh();
    },
    isTableEmpty(isEmpty) {
      if (!isEmpty) {
        this.$nextTick(() => {
          this.cellTruncation?.onTableBecameNonEmpty();
        });
      }
    },
    page_details: {
      handler() {
        if (this.hasCustomTbodySlot()) {
          this.cellTruncation?.onSlotPaginationChange();
        }
      },
      deep: true,
    },
    selectfilter: {
      handler(newVal) {
        // If we already have a persisted filter and parent sends empty, keep persisted
        try {
          const persisted = this._loadPersistedFilterFromStore();
          if (persisted && (!newVal || Object.keys(newVal).length === 0)) {
            // Don't override with empty incoming; ensure we have something applied
            if (Object.keys(this.selectedFilters || {}).length === 0) {
              // Validate persisted filter keys before applying
              const validatedPersisted = this._validateFilterKeys(persisted);
              this.selectedFilters = validatedPersisted;
              this.isFilterSavedToBackend = Object.keys(validatedPersisted).length > 0;
              this._applyFilterIfChanged(validatedPersisted, { force: true });
              this.syncStatuses();
            }
            return; // skip further processing
          }
        } catch(e) { 
          // Log the error for debugging, but continue gracefully
          console.error(`${LOG_PREFIX} Error loading persisted filter from store:`, e); 
        }

        if (!this.applyFilterOnChange) {
          this.selectedFilters = newVal;  // just sync local copy
          return;
        }
        // Validate incoming filter keys before applying
        const validatedFilter = this._validateFilterKeys(newVal || {});
        this.selectedFilters = validatedFilter;
        
        // Mark as saved to backend if incoming filter has data and save option is enabled
        // This handles the case where filter is loaded from getCustomLayout API
        if (Object.keys(validatedFilter).length > 0 && this.computedShowFilterSaveButton) {
          this.isFilterSavedToBackend = true;
        }
        
        this._scheduleFilter(validatedFilter);
        this.syncStatuses();
      },
      deep: true,
      immediate: false,   // CHANGED: was true (caused double-run with mounted)
    },
    /**
     * Watch hasActiveFilter computed and emit to parent for external clear button
     */
    hasActiveFilter: {
      handler(newVal) {
        this.$emit('filter-active-change', newVal);
      },
      immediate: true
    },
    menuOpen: {
      handler(val) {
        const isOpen = Array.isArray(val) ? val.some(Boolean) : false;
        this.$emit('column-menu-change', isOpen);
      },
      deep: true,
    },
    // selectedHeader() {
    //   // Rebuild unique value list only when user changes target column
    //   this.filteredUniqueDataItems();
    // }
  },
  methods:{
    getHeaderStickyMeta(headerIndex) {
      const count = Number(this.stickyColumnCount || 0);
      if (!count) return null;

      let domIndex = this.bulkActions ? 1 : 0;
      const headers = this.computedHeaders;
      for (let i = 0; i < headers.length; i++) {
        if (!headers[i].visible) continue;
        if (i === headerIndex) {
          if (domIndex >= count) return null;
          return {
            stickySeq: domIndex,
            isEdge: domIndex === count - 1,
          };
        }
        domIndex += 1;
      }
      return null;
    },
    getHeaderStickySeq(headerIndex) {
      const meta = this.getHeaderStickyMeta(headerIndex);
      return meta != null ? String(meta.stickySeq) : undefined;
    },
    getSelectAllHeaderClass() {
      const count = Number(this.stickyColumnCount || 0);
      const base = ['pl-4', this.headerBgClass, 'th-width-48'];
      if (!count || !this.bulkActions) return base;
      return [
        ...base,
        'dyn-sticky-col',
        { 'dyn-sticky-col-edge': count === 1 },
      ];
    },
    /**
     * Public method to clear filters - can be called via $refs from parent
     */
    clearFilter() {
      this.clearfilter();
    },
    /**
     * Resolve teleported v-tooltip content for this table instance only.
     * @returns {Element|null}
     */
    resolveCellTooltipContentEl() {
      const tipRef = this.$refs.cellTooltipRef?.$refs?.tooltipRef || this.$refs.cellTooltipRef;
      const scopedClass = this.cellTooltipContentClass;
      const scopedSelector = `.${scopedClass}`;

      const contentRef = tipRef?.$refs?.content;
      const fromTooltipRef =
        (contentRef && contentRef.nodeType === 1 ? contentRef : null) ||
        contentRef?.$el;
      if (fromTooltipRef?.classList?.contains(scopedClass)) {
        return fromTooltipRef;
      }

      const fromActivatorTree = tipRef?.$el?.querySelector?.(scopedSelector);
      if (fromActivatorTree) return fromActivatorTree;

      return (
        document.querySelector(`${scopedSelector}.menuable__content__active`) ||
        document.querySelector(scopedSelector)
      );
    },
    // DRY: shared initialization for mounted & activated
    _initFilters({ force = false } = {}) {
      // Prefer persisted filter if present; else fall back to incoming prop
      const persisted = this._loadPersistedFilterFromStore();
      // Validate filter keys against current headers to remove stale entries
      const validatedFilter = this._validateFilterKeys(persisted || this.selectfilter || {});
      const base = validatedFilter;
      this.selectedFilters = base;
      
      // Mark filter as saved to backend if:
      // 1. We loaded from local persistence store, OR
      // 2. Filter came from selectfilter prop AND save option is enabled (means it came from getCustomLayout API)
      const hasPersistedFilter = !!(persisted && Object.keys(persisted).length > 0);
      const hasIncomingFilter = !!(this.selectfilter && Object.keys(this.selectfilter).length > 0);
      this.isFilterSavedToBackend = hasPersistedFilter || (hasIncomingFilter && this.computedShowFilterSaveButton);
      
      this._applyFilterIfChanged(base, { force });
      // Rebuild unique items if a header already chosen
      if (this.selectedHeader) {
        this.filteredUniqueDataItems();
        this.syncStatuses();
      } else {
        // Still ensure internal statuses are consistent
        this.syncStatuses();
      }
    },
    /**
     * Validates filter keys against current headers to remove stale/invalid filter entries
     * This prevents errors when filter columns have been deleted from the system
     * @param {Object} filter - The filter object to validate
     * @returns {Object} - Cleaned filter with only valid keys
     */
    _validateFilterKeys(filter) {
      if (!filter || typeof filter !== 'object') return {};
      
      // Only keep filters for visible, filterable columns
      const validHeaderValues = new Set();
      const headersToCheck = this.headers || [];
      headersToCheck.forEach(h => {
        if (!h?.value) return;
        if (h.visible === false || h.filterable === false) return;
        validHeaderValues.add(h.value);
        if (h?.value_en) validHeaderValues.add(h.value_en);
      });
      
      // Headers not ready yet — preserve incoming filter until layout is loaded
      if (validHeaderValues.size === 0) {
        return headersToCheck.length === 0 ? filter : {};
      }
      
      // Clean filter by removing keys that don't match any header
      const cleanedFilter = {};
      let hasInvalidKeys = false;
      
      Object.keys(filter).forEach(filterKey => {
        if (validHeaderValues.has(filterKey)) {
          cleanedFilter[filterKey] = filter[filterKey];
        } else {
          hasInvalidKeys = true;
          console.warn(`${LOG_PREFIX} Removed stale filter key: ${filterKey}`);
        }
      });
      
      // If we cleaned up invalid keys, update persisted storage as well
      if (hasInvalidKeys) {
        this.persistFilter(cleanedFilter);
      }
      
      return cleanedFilter;
    },
    filteredUniqueDataItems() {
      // retained for compatibility; actual unique building now handled in ColumnFilter
      return [];
    },
  /**
   * Updates the status of each item in filteredColumnItems based on the current filter selection.
   * Handles the NONE_SELECTED sentinel logic.
   */
    syncStatuses() {
      if (!this.selectedHeader || !this.selectedHeader.value) return;
      const col = this.selectedHeader.value;
      const selected = this.selectedFilters[col] || [];
      const noneSelectedMode = selected.includes(this.noneSentinel);
      const isFiltered = selected.length > 0 && !noneSelectedMode;
      this.filteredColumnItems.forEach(obj => {
        obj.status = noneSelectedMode ? false : (!isFiltered || selected.includes(obj.value));
      });
      // TODO - this needs to be updated when searched and set
    },
    pagecount() {
      return Math.ceil(this.tabledata?.length / this.itemsperpage);
    },
    resetStage() {
      this.filterStage = 0;
      this.selectedFilterItems = [];
    },
    onFilterByClick(header) {
      this.selectedHeader = header;
      this.searchfilters = "";
      this.filteredUniqueDataItems();
      this.syncStatuses();
      this.filterStage = 1;
    },
    handleMenuClose(isOpen) {
      if (!isOpen) {
        // this.resetStage(); 
      }
    },
    shouldPreventColumnDrag(event) {
      const target = event && event.target;
      if (!target || !target.closest) {
        return true;
      }
      if (target.closest('.resizer-handle')) {
        return true;
      }
      if (target.closest('.data-table-action__column-actions-btn')) {
        return true;
      }
      if (target.closest('button, input, textarea, select, a, label')) {
        return true;
      }
      return false;
    },
    startDrag(index, event) {
      if (!this.enableColumnReorder) {
        if (event && event.preventDefault) event.preventDefault();
        return;
      }
      if (this.shouldPreventColumnDrag(event)) {
        if (event && event.preventDefault) event.preventDefault();
        return;
      }
      this.draggingIndex = index;
      this.dropTargetIndex = null;
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(index));
        const headerCell = event.currentTarget && event.currentTarget.closest
          ? event.currentTarget.closest('th')
          : null;
        if (headerCell && event.dataTransfer.setDragImage) {
          event.dataTransfer.setDragImage(headerCell, event.offsetX, event.offsetY);
        }
      }
    },
    onDragOver(index, event) {
      if (this.draggingIndex === null || this.draggingIndex === index) {
        if (this.draggingIndex === index) this.dropTargetIndex = null;
        return;
      }
      if (event && event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
      if (this.dropTargetIndex !== index) {
        this.dropTargetIndex = index;
      }
    },
    onDragLeave(index, event) {
      // HTML5 DnD often fires dragleave with null relatedTarget when moving
      // over child nodes — only clear when we truly leave this header cell.
      const relatedTarget = event && event.relatedTarget;
      if (!relatedTarget) return;
      if (event.currentTarget && event.currentTarget.contains(relatedTarget)) return;
      if (this.dropTargetIndex === index) {
        this.dropTargetIndex = null;
      }
    },
    onDragEnd() {
      this.draggingIndex = null;
      this.dropTargetIndex = null;
    },
    updateFixedDivHeight() {
      this.fixedDivHeight = this.$refs.fixedDiv.offsetHeight;
    },
    onDrop(dropIndex) {
      const dragIndex = this.draggingIndex;
      if (dragIndex === null || dragIndex === dropIndex) {
        this.onDragEnd();
        return;
      }
      const baseHeaders = this.localHeaders.length > 0 ? this.localHeaders : this.headers;
      const reordered = [...baseHeaders];
      const [draggedHeader] = reordered.splice(dragIndex, 1);
      let insertIndex = dropIndex;
      if (dragIndex < dropIndex) {
        insertIndex = dropIndex - 1;
      }
      reordered.splice(insertIndex, 0, draggedHeader);
      this.localHeaders = reordered;
      this.onDragEnd();
      this.$emit('column-reorder', [...reordered]);
      this.$emit('update:headers', [...reordered]);
      this.$nextTick(() => this.syncBodyColumnOrder());
    },
    getVisibleHeaderValues(headerList) {
      return (headerList || [])
        .filter((header) => header && header.visible !== false)
        .map((header) => header.value);
    },
  /**
   * Detect whether the tbody slot binds scoped `headers` (Vue re-renders body on reorder).
   * When false, parent iterates its own headers prop and DOM sync is required.
   */
    detectSlotUsesScopedHeaders() {
      const slotFn = this.$scopedSlots && this.$scopedSlots.tbody;
      if (!slotFn) {
        return false;
      }
      const headers = this.computedHeaders;
      if (!headers || headers.length < 2) {
        return false;
      }
      const visibleIndices = headers
        .map((header, idx) => (header.visible !== false ? idx : -1))
        .filter((idx) => idx >= 0);
      if (visibleIndices.length < 2) {
        return false;
      }
      const swappedHeaders = JSON.parse(JSON.stringify(headers));
      const firstVisible = visibleIndices[0];
      const secondVisible = visibleIndices[1];
      [swappedHeaders[firstVisible], swappedHeaders[secondVisible]] = [
        swappedHeaders[secondVisible],
        swappedHeaders[firstVisible],
      ];
      try {
        const defaultVnodes = slotFn({ headers });
        const swappedVnodes = slotFn({ headers: swappedHeaders });
        return this.serializeVnodeTree(defaultVnodes) !== this.serializeVnodeTree(swappedVnodes);
      } catch (error) {
        console.error(`${LOG_PREFIX} detectSlotUsesScopedHeaders failed:`, error);
        return false;
      }
    },
    serializeVnodeTree(vnodes) {
      if (!vnodes) {
        return '';
      }
      if (Array.isArray(vnodes)) {
        return vnodes.map((vnode) => this.serializeVnodeTree(vnode)).join('|');
      }
      const tag = vnodes.tag || vnodes.componentOptions?.Ctor?.options?.name || '';
      const key = vnodes.key != null ? String(vnodes.key) : '';
      const props = vnodes.componentOptions?.propsData;
      const headersSig = props?.headers
        ? props.headers.map((header) => header.value).join(',')
        : '';
      const children = vnodes.componentOptions?.children || vnodes.children;
      const childSig = children ? this.serializeVnodeTree(children) : '';
      return `${tag}:${key}:${headersSig}:${childSig}`;
    },
    /**
     * Reorder tbody cells to match header order when slot content ignores scoped headers.
     */
    syncBodyColumnOrder(explicitSourceOrder = null, explicitTargetOrder = null) {
      if (!this.enableColumnReorder || this.isTableEmpty) {
        return;
      }
      if (this.slotUsesScopedHeaders === undefined) {
        this.slotUsesScopedHeaders = this.detectSlotUsesScopedHeaders();
      }
      if (this.slotUsesScopedHeaders) {
        return;
      }
      const targetOrder = explicitTargetOrder || this.getVisibleHeaderValues(this.computedHeaders);
      const targetKey = targetOrder.join('\0');
      if (!explicitSourceOrder && !explicitTargetOrder && this.lastBodySyncOrderKey === targetKey) {
        return;
      }
      let sourceOrder = explicitSourceOrder;
      if (!sourceOrder) {
        if (this.lastBodySyncOrderKey) {
          sourceOrder = this.lastBodySyncOrderKey.split('\0');
        } else {
          sourceOrder = this.getVisibleHeaderValues(this.headers);
        }
      }
      if (sourceOrder.join('\0') === targetKey) {
        this.lastBodySyncOrderKey = targetKey;
        return;
      }
      const tbody = this.$el && this.$el.querySelector('tbody.data-table-action__tbody');
      if (!tbody) {
        return;
      }
      const dataColCount = targetOrder.length;
      if (dataColCount === 0) {
        return;
      }
      const indexMap = targetOrder.map((value) => sourceOrder.indexOf(value));
      if (indexMap.some((idx) => idx < 0)) {
        return;
      }
      const rows = tbody.querySelectorAll('tr:not(.data-table-action__empty-row)');
      rows.forEach((row) => {
        const cells = [...row.children];
        const leadingCount = cells.length - dataColCount;
        if (leadingCount < 0) {
          return;
        }
        const dataCells = cells.slice(leadingCount);
        if (dataCells.length !== dataColCount) {
          return;
        }
        const reorderedCells = indexMap.map((idx) => dataCells[idx]);
        reorderedCells.forEach((cell) => row.appendChild(cell));
      });
      this.lastBodySyncOrderKey = targetKey;
      this.scheduleTruncationRefresh();
    },
    getItemData(item, key) {
      return item[key];
    },
    hasCustomTbodySlot() {
      return this.cellTruncation?.hasCustomTbodySlot() ?? false;
    },
    getHeaderColumnStyle(header) {
      return this.cellTruncation?.getHeaderColumnStyle(header) ?? {};
    },
    scheduleTruncationRefresh() {
      this.cellTruncation?.scheduleTruncationRefresh();
    },
    syncBodyColumnWidth(headerIndex, maxWidthPx) {
      this.cellTruncation?.syncBodyColumnWidth(headerIndex, maxWidthPx);
    },
    initResize(event, index) {

      this.currentIndex = index;
      this.startX = event.clientX;
      this.startWidth = event.target.parentElement.offsetWidth;
      document.documentElement.addEventListener('mousemove', this.doResize, false);
      document.documentElement.addEventListener('mouseup', this.stopResize, false);
    },
    doResize(event) {
      const maxWidth = this.startWidth + (event.clientX - this.startX);
      const header = this.$refs['header' + this.currentIndex][0];
      if (header) {
        const styleString = `max-width: ${maxWidth}px !important;`;
        header.setAttribute('style', styleString);
        this.syncBodyColumnWidth(this.currentIndex, maxWidth);
      }
    },
    stopResize() {
      document.documentElement.removeEventListener('mousemove', this.doResize, false);
      document.documentElement.removeEventListener('mouseup', this.stopResize, false);
      this.scheduleTruncationRefresh();
    },
    isHeaderVisible(value) {
      const header = this.headers.find(header => header.value === value && header.visible === true);
      return header ? header.visible : false;
    },
    openColumnActions(item, index) {
      if (this.columnActionsDisabled) return;
      this.clickedHeader = item.value;
      this.filterStage = 0;
      this.$set(this.menuOpen, index, true);
    },
    onSelectAllChange(value) {
      // Use the change-event payload — not this.selectAll.
      // v-model also listens to `change`; if this handler runs first,
      // this.selectAll is still the previous value and would clear selection.
      const checked = typeof value === 'boolean' ? value : Boolean(this.selectAll);
      this.selectAll = checked;
      this.$emit('select-all-change', checked);
    },
    _scheduleFilter(filterObj) {
      if (this.debounceFilterMs <= 0) {
        this._applyFilterIfChanged(filterObj);
        return;
      }
      this.pendingFilter = filterObj;
      clearTimeout(this._filterTimer);
      this._filterTimer = setTimeout(() => {
        this._applyFilterIfChanged(this.pendingFilter);
        this.pendingFilter = null;
      }, this.debounceFilterMs);
    },
    // Stable JSON signature with sorted keys
    _stableSignature(obj) {
      if (!obj || typeof obj !== 'object') return '';
      return JSON.stringify(this._sortObject(obj));
    },
    _sortObject(o) {
      if (Array.isArray(o)) return o.map(v => this._sortObject(v));
      if (o && typeof o === 'object') {
        return Object.keys(o).sort().reduce((acc, fieldKey) => {
          acc[fieldKey] = this._sortObject(o[fieldKey]);
          return acc;
        }, {});
      }
      return o;
    },
    _applyFilterIfChanged(newFilter, { force = false } = {}) {
      const sig = this._stableSignature(newFilter);
      if (!force && sig === this.lastAppliedFilterSignature) return;
      this.lastAppliedFilterSignature = sig;
      this.applyTableFilter(newFilter);
      if (this.selectedHeader) this.filteredUniqueDataItems();
      // Removed forceRerender(); Vue will patch DOM efficiently without key reset
    },
    // --- Persistence helpers (Vuex) ---
    _persistStorageKey() {
  const pageName = this.page_details?.pagename || 'unknown';
  const scopeIdentifier = this.page_details?.cycle_id || this.$store?.state?.client || 'global';
  return `dt_filter:${pageName}:${scopeIdentifier}`;
    },
    _loadPersistedFilterFromStore() {
      // Only load persisted filter if page_details has a valid pagename
      if (!this.page_details?.pagename) {
        return null;
      }
      const key = this._persistStorageKey();
      return this.$store.state.tableFilters[key] || null;
    },
    persistFilter(filterObj) {
      // Only persist filter if page_details has a valid pagename
      if (!this.page_details?.pagename) {
        return;
      }
      if (!filterObj || typeof filterObj !== 'object') return;
      const cleaned = Object.keys(filterObj).reduce((acc, fieldKey)=>{
        const fieldValue = filterObj[fieldKey];
        if (Array.isArray(fieldValue)) {
          if (fieldValue.length > 0) acc[fieldKey] = fieldValue.slice();
        } else if (fieldValue !== undefined && fieldValue !== null) {
          acc[fieldKey] = fieldValue;
        }
        return acc;
      }, {});
      const key = this._persistStorageKey();
      if (Object.keys(cleaned).length > 0) {
        this.$store.commit('setTableFilter', { key, filter: cleaned });
      } else {
        this.$store.commit('clearTableFilter', key);
      }
    },
    clearPersistedFilter() {
      const key = this._persistStorageKey();
      this.$store.commit('clearTableFilter', key);
    },
    applyTableFilter(item){  
      this.persistFilter(item);
      this.$emit('filter-item',item);
    },
    /**
     * Clears all active filters.
     * Uses Vuex action for backend API calls (architecture compliance).
     * Only calls the backend API if:
     * 1. showFilterSaveButton is true (persistence is enabled for this table)
     * 2. Filter was actually saved to backend (isFilterSavedToBackend flag)
     * Otherwise, just clears local state without API call.
     */
    async clearfilter(){
      this.selectedHeader = null;
      this.loading = true;
      
      try {
        // Check if we should call backend API
        const hasPageDetails = Object.keys(this.page_details || {}).length !== 0;
        const shouldCallBackendApi = hasPageDetails && this.computedShowFilterSaveButton && this.isFilterSavedToBackend;
        
        if (shouldCallBackendApi) {
          // Filter was saved to backend - dispatch Vuex action to remove
          await this.$store.dispatch('clearCustomLayout', {
            axios: this.axios,
            page: this.page_details.pagename,
            cycleId: this.page_details.cycle_id,
          });
        }
        
        // Always clear local state regardless of API call result
        this._resetFilterState();
        
        // Show success message if we didn't call API (local clear only)
        if (!shouldCallBackendApi && hasPageDetails) {
          this.$store.commit('sbSuccess', 'Filter cleared');
        }
        
      } catch (err) {
        // Handle API errors gracefully - still clear local state
        console.error(`${LOG_PREFIX} Error clearing filter:`, err);
        // Don't show error to user if it's just a "key not found" error - the filter is effectively cleared
        const errorMessage = err?.response?.data?.message || err.message || '';
        if (errorMessage.toLowerCase().includes('not found')) {
          // Key not found means filter was already cleared or never saved - this is OK
          console.warn(`${LOG_PREFIX} Filter key not found in backend - clearing local state only`);
        }
        // Note: Vuex action already commits sbError, so we don't duplicate here
        
        // Still clear local state even on error
        this._resetFilterState();
      } finally {
        this.loading = false;
      }
    },
    /**
     * Helper method to reset all filter-related state.
     * Extracted to reduce code duplication in clearfilter().
     */
    _resetFilterState() {
      this.selectedFilters = {};
      this.isFilterSavedToBackend = false;
      this.lastAppliedFilterSignature = '';
      this.clearPersistedFilter();
      this.applyTableFilter({});
      this.$emit('clear-item');
      this.filterStage = 0;
      this.menuOpen = [];
    },
    async saveFilter(filter){
      // Clone and clean filter: remove empty arrays; translate NONE_SELECTED sentinel to empty special marker
      // Also validate filter keys against current headers before saving
      const validatedFilter = this._validateFilterKeys(filter || {});
      const cleaned = {};
      Object.keys(validatedFilter).forEach(fieldKey => {
        const arr = validatedFilter[fieldKey];
        if (Array.isArray(arr)) {
          if (arr.includes(this.noneSentinel)) {
            // Represent explicit "no values" selection with sentinel for backend if required
            cleaned[fieldKey] = [this.noneSentinel];
          } else if (arr.length > 0) {
            cleaned[fieldKey] = arr.slice();
          }
        } else if (arr && typeof arr === 'object') {
          // date-range { from, to } or number-range { min, max }: include if at least one bound is set
          if (Object.values(arr).some(v => v !== null && v !== undefined && v !== '')) {
            cleaned[fieldKey] = { ...arr };
          }
        }
      });
      
      // If no valid filters remain after validation
      if (Object.keys(cleaned).length === 0) {
        // If filter was previously saved to backend, clear it (user selected all = clear filter)
        if (this.isFilterSavedToBackend) {
          await this.clearfilter();
          return;
        }
        // No previous saved filter and nothing to save - just apply empty filter locally
        this._resetFilterState();
        return;
      }
      
      try {
        await this.$store.dispatch('saveCustomLayout', {
          axios: this.axios,
            page: this.page_details.pagename,
            cycleId: this.page_details?.cycle_id,
            layout: this.page_details?.layout,
            filter: cleaned
        });
        // Mark filter as saved to backend
        this.isFilterSavedToBackend = true;
      } catch (err) {
        this.$store.commit('sbError', err.message || err || 'Unknown error!');
        console.error(`${LOG_PREFIX} Error saving filter:`, err);
      } finally {
        this.loading = false;
        this.filterStage = 0;
      }
    },
    sortBy(header,order){
      this.$emit('sort-order',header,order);
    },
     forceRerender() {
      this._applyFilterIfChanged(this.selectedFilters, { force: true });
    },
    onCheckboxChange() {
      // UPDATED: delegate to new logic (kept for compatibility if referenced)
      // no-op after refactor; retained for backward compatibility
    },
    handleFilterChange(values) {
      if (!this.selectedHeader || !this.selectedHeader.value) return;
      const col = this.selectedHeader.value;
      this.$set(this.selectedFilters, col, values);
      this.applyTableFilter(this.selectedFilters);
    },
    /**
     * Handle Apply button click from ColumnFilter.
     * Applies filter locally without persisting to backend.
     * @param {Array} values - Selected filter values
     * @param {Number} menuIndex - Index of the menu to close
     */
    handleFilterApply(values, menuIndex) {
      if (!this.selectedHeader || !this.selectedHeader.value) return;
      const col = this.selectedHeader.value;
      this.$set(this.selectedFilters, col, values);
      this.applyTableFilter(this.selectedFilters);
      // Close the menu after applying
      this.closeMenu(menuIndex);
    },
    /**
     * Handle Save button click from ColumnFilter.
     * Applies filter locally AND persists to backend.
     * @param {Array} values - Selected filter values
     * @param {Number} menuIndex - Index of the menu to close
     */
    handleFilterSave(values, menuIndex) {
      if (!this.selectedHeader || !this.selectedHeader.value) return;
      const col = this.selectedHeader.value;
      this.$set(this.selectedFilters, col, values);
      this.applyTableFilter(this.selectedFilters);
      // Save to backend
      this.saveFilter(this.selectedFilters);
      // Close the menu after saving
      this.closeMenu(menuIndex);
    },
    handleSaveFilter() {
      // Legacy method - kept for backward compatibility
      this.saveFilter(this.selectedFilters);
    },
    closeMenu(index) {
      this.$set(this.menuOpen, index, false);
      this.filterStage = 0;
    },
  
  },
};
</script>
